/**
 * Storage do navegador (plano, seções 15.3 e 19.2): Dexie sobre IndexedDB.
 * Funciona na página e no service worker (Background Sync).
 */
import type { EntityMap, EntityType, OutboxEntry, Storage } from '@lindeza/core';
import { ENTITY_TABLES, ENTITY_TYPES } from '@lindeza/core';
import Dexie, { type Table } from 'dexie';

type Row = Record<string, unknown> & { id: string };
interface OutboxRow extends OutboxEntry {
  seq?: number;
}
interface MetaRow {
  key: string;
  value: unknown;
}

const INDEXES: Record<EntityType, string> = {
  professional: 'id',
  service: 'id',
  client: 'id, whatsapp',
  availability_rule: 'id',
  availability_window: 'id, date',
  block: 'id, start_utc',
  appointment: 'id, start_utc, client_id',
  payment: 'id, paid_at, appointment_id, client_id',
  package: 'id, client_id',
  message_template: 'id',
  product: 'id',
  stock_movement: 'id, product_id, occurred_at, appointment_id, sale_id',
  sale: 'id, sold_at, appointment_id',
  service_supply: 'id, service_id',
  appointment_service: 'id, appointment_id, service_id',
};

const INDEXED_FIELDS: Record<EntityType, string[]> = Object.fromEntries(
  ENTITY_TYPES.map((e) => [e, INDEXES[e].split(',').map((s) => s.trim()).slice(1)]),
) as Record<EntityType, string[]>;

class LindezaDexie extends Dexie {
  outbox!: Table<OutboxRow, number>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    const stores: Record<string, string> = {
      outbox: '++seq, &op_id, [entity+id]',
      meta: 'key',
    };
    for (const e of ENTITY_TYPES) stores[ENTITY_TABLES[e]] = INDEXES[e];
    // v2: estoque (produtos, movimentos, vendas e gasto por serviço).
    // v3: serviços do atendimento (vários por atendimento).
    // Só tabelas novas nas duas: o Dexie cria sem migração de dados.
    this.version(3).stores(stores);
  }

  entity(e: EntityType): Table<Row, string> {
    return this.table(ENTITY_TABLES[e]);
  }
}

export interface DexieStorageOptions {
  /** nome do banco no IndexedDB */
  name?: string;
  /** canal para avisar outras abas e o service worker */
  channelName?: string;
}

export class DexieStorage implements Storage {
  readonly db: LindezaDexie;
  private channel: BroadcastChannel | null = null;
  private readonly channelName: string;

  constructor(options: DexieStorageOptions = {}) {
    this.db = new LindezaDexie(options.name ?? 'lindeza');
    this.channelName = options.channelName ?? 'lindeza-db';
    if (typeof BroadcastChannel !== 'undefined') this.channel = new BroadcastChannel(this.channelName);
  }

  async getAll<E extends EntityType>(entity: E): Promise<EntityMap[E][]> {
    return (await this.db.entity(entity).toArray()) as unknown as EntityMap[E][];
  }

  async getRange<E extends EntityType>(
    entity: E,
    field: 'start_utc' | 'client_id' | 'date',
    lower: string,
    upper: string,
  ): Promise<EntityMap[E][]> {
    const table = this.db.entity(entity);
    if (INDEXED_FIELDS[entity].includes(field)) {
      return (await table.where(field).between(lower, upper, true, true).toArray()) as unknown as EntityMap[E][];
    }
    const all = await table.toArray();
    return all.filter((r) => {
      const v = r[field];
      return typeof v === 'string' && v >= lower && v <= upper;
    }) as unknown as EntityMap[E][];
  }

  async get<E extends EntityType>(entity: E, id: string): Promise<EntityMap[E] | undefined> {
    return (await this.db.entity(entity).get(id)) as unknown as EntityMap[E] | undefined;
  }

  async put<E extends EntityType>(entity: E, record: EntityMap[E]): Promise<void> {
    await this.db.entity(entity).put(record as unknown as Row);
  }

  async bulkPut<E extends EntityType>(entity: E, records: EntityMap[E][]): Promise<void> {
    await this.db.entity(entity).bulkPut(records as unknown as Row[]);
  }

  async enqueue(entry: OutboxEntry): Promise<void> {
    const { seq: _ignored, ...rest } = entry;
    await this.db.outbox.add(rest);
  }

  async peekOutbox(limit: number): Promise<OutboxEntry[]> {
    return this.db.outbox.orderBy('seq').limit(limit).toArray();
  }

  async outboxFor(entity: EntityType, id: string): Promise<OutboxEntry[]> {
    const rows = await this.db.outbox.where('[entity+id]').equals([entity, id]).toArray();
    return rows.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  }

  async removeOutbox(opIds: string[]): Promise<void> {
    if (opIds.length === 0) return;
    await this.db.outbox.where('op_id').anyOf(opIds).delete();
  }

  async countOutbox(): Promise<number> {
    return this.db.outbox.count();
  }

  async getMeta<T = unknown>(key: string): Promise<T | undefined> {
    return (await this.db.meta.get(key))?.value as T | undefined;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    if (value === undefined) await this.db.meta.delete(key);
    else await this.db.meta.put({ key, value });
  }

  async transaction<T>(fn: () => Promise<T>): Promise<T> {
    return this.db.transaction('rw', this.db.tables, fn);
  }

  async clear(): Promise<void> {
    await this.db.transaction('rw', this.db.tables, async () => {
      await Promise.all(this.db.tables.map((t) => t.clear()));
    });
    this.notifyExternal([...ENTITY_TYPES]);
  }

  onExternalChange(listener: (entities: EntityType[]) => void): () => void {
    if (typeof BroadcastChannel === 'undefined') return () => undefined;
    // Canal próprio para receber (o mesmo objeto não recebe as próprias mensagens).
    const rx = new BroadcastChannel(this.channelName);
    rx.onmessage = (ev: MessageEvent<{ entities?: EntityType[] }>) => {
      if (Array.isArray(ev.data?.entities)) listener(ev.data.entities);
    };
    return () => rx.close();
  }

  notifyExternal(entities: EntityType[]): void {
    try {
      this.channel?.postMessage({ entities });
    } catch {
      // aba fechando: ignorar
    }
  }

  /** Pede ao navegador para não apagar os dados (seção 19.2, item 6). */
  static async requestPersistence(): Promise<boolean> {
    try {
      // persist() só existe na página (não no service worker)
      const storage = (typeof navigator !== 'undefined' ? navigator.storage : undefined) as
        | (StorageManager & { persist?: () => Promise<boolean> })
        | undefined;
      if (storage?.persist) {
        if (await storage.persisted()) return true;
        return await storage.persist();
      }
    } catch {
      // navegador sem suporte
    }
    return false;
  }
}
